import { ArrowFloorButton, FloorButton, SecondaryFloorButton } from '@/components/feature/floor-guide';
import { ChevronDownIcon, ChevronUpIcon } from '@/components/icons';
import { FLOOR_LIST_CONFIG } from '@/constants/floor-guide';
import { useAccessibilityTTS, useDisableWheelEvent, useScreenSize } from '@/hooks';
import { useFloorManagement } from '@/hooks/floor-guide';
import { useFloorCategory } from '@/hooks/map/useFloorCategory';
import { useConfigStore } from '@/stores';
import { AppUtils } from '@/utils/app';
import { generateAllFloorsGuideWithTTS, generateFloorName, generateFloorNameWithTTS } from '@/utils/floor-guide';
import clsx from 'clsx';
import type { IDataFloor } from 'dabeeomaps/dist/src/model/map/IDataFloor';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';
import styles from './styles.module.scss';

type Props = {
    floors?: IDataFloor[];
    selectedFloorName?: string;
    isShowAllFloorGuide: boolean;
    onSelectFloor?: (floorName: string) => void;
    setIsShowAllFloorGuide: (isShow: boolean) => void;
    onShowMyLocation: () => void;
    currentMapBuildingId?: string;
};

export const HorizontalFloorList = ({ floors, selectedFloorName, onSelectFloor, isShowAllFloorGuide, setIsShowAllFloorGuide, onShowMyLocation, currentMapBuildingId }: Props) => {
    const { t, i18n } = useTranslation();
    const { selectedFloor } = useFloorCategory(selectedFloorName, currentMapBuildingId);
    const { scaleHeightPixel } = useScreenSize();
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const { speak } = useAccessibilityTTS();
    const isFullscreen = screenMode === 'fullscreen';
    const buttonText = t('common.button.tts.button');
    const inactiveButtonText = t('common.button.tts.inactive_button');

    const [lastInteractionWasMouse, setLastInteractionWasMouse] = useState(false);

    const handleMyLocationMouseDown = () => {
        setLastInteractionWasMouse(true);
    };

    const handleMyLocationFocus = () => {
        if (!lastInteractionWasMouse) {
            speak(`${t('floor_guide.button.current_location')} ${t('common.data_role.button')}`);
        }
        setLastInteractionWasMouse(false); // reset
    };

    // State to track current visible index of Swiper
    const [currentActiveIndex, setCurrentActiveIndex] = useState(0);

    const swiperContainerRef = useRef<HTMLDivElement>(null);

    const slidesPerView = isFullscreen ? FLOOR_LIST_CONFIG.FULLSCREEN.SLIDES_PER_VIEW : FLOOR_LIST_CONFIG.NORMAL.SLIDES_PER_VIEW;
    const amountToShow = isFullscreen ? FLOOR_LIST_CONFIG.FULLSCREEN.AMOUNT_TO_SHOW : FLOOR_LIST_CONFIG.NORMAL.AMOUNT_TO_SHOW;

    const { handleScrollUp, handleScrollDown, shouldScroll, swiperRef, handleDisableNext, handleDisablePrev, isDisabledPrev, isDisabledNext, floorSlides } =
        useFloorManagement(floors, amountToShow, selectedFloorName, slidesPerView);

    const handleAllFloorsGuide = () => {
        setIsShowAllFloorGuide(!isShowAllFloorGuide);
    };

    const isSelectedItem = useCallback(
        (floor?: IDataFloor) => {
            if (!floor || !Array.isArray(floor.name) || !floor.name?.length) {
                return false;
            }
            return floor.name[0]?.text === selectedFloorName;
        },
        [selectedFloorName]
    );

    // Function to determine if a floor button should have tabIndex based on active slide
    const getFloorTabIndex = useCallback(
        (slideIndex: number) => {
            // Only items in the currently active slide should be focusable
            if (slideIndex !== currentActiveIndex) return -1;

            // Calculate the actual tabIndex: base 4 + item position in slide
            return 4;
        },
        [currentActiveIndex]
    );

    const handleSelectFloor = useCallback(
        (floor?: IDataFloor) => {
            if (!floor || !Array.isArray(floor.name) || !floor.name?.length) {
                return;
            }
            const floorName = floor.name[0]?.text ?? '';
            speak(t('floor_guide.text_to_speech.move_to_floor', { floorName: generateFloorName(floorName) }));
            onSelectFloor?.(floorName);
        },
        [onSelectFloor, speak, t]
    );

    const handleFocusFloorBtn = useCallback(
        (floor: IDataFloor, selected: boolean) => {
            if (!floor || !Array.isArray(floor.name) || !floor.name?.length) return;
            speak(
                generateFloorNameWithTTS(
                    t,
                    t('floor_guide.text_to_speech.floor', {
                        floorName: generateFloorName(floor.name[0]?.text ?? ''),
                    }),
                    selected
                )
            );
        },
        [speak, t]
    );

    // Voice handling for arrow buttons
    const handleScrollUpWithTTS = useCallback(() => {
        if (isDisabledPrev) {
            speak(t('floor_guide.text_to_speech.click.inactive_prev_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_prev_floor_btn'));
            handleScrollUp();
        }
    }, [handleScrollUp, isDisabledPrev, speak, t]);

    const handleScrollDownWithTTS = useCallback(() => {
        if (isDisabledNext) {
            speak(t('floor_guide.text_to_speech.click.inactive_next_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_next_floor_btn'));
            handleScrollDown();
        }
    }, [handleScrollDown, isDisabledNext, speak, t]);

    // Use custom hook to disable wheel events on Swiper container
    useDisableWheelEvent(swiperContainerRef);

    return (
        <section className={styles['floor-wrapper']}>
            <section className={styles['floor__selected-floor']} data-region-nav data-region-order={1}>
                {!isShowAllFloorGuide ? (
                    <p
                        tabIndex={1}
                        className={styles['floor__selected-floor__text'] + ' focus-border number'}
                        data-interactive
                        aria-label={t('floor_guide.text_to_speech.floor', {
                            floorName: AppUtils.getFloorTTSText(selectedFloor?.lotteName?.[i18n.language] || ''),
                        })}
                    >
                        {selectedFloor?.lotteName?.[i18n.language] ?? selectedFloor?.name?.['ko']}
                    </p>
                ) : (
                    <p
                        tabIndex={1}
                        className={styles['floor__selected-floor__all-floors-guide'] + ' focus-border'}
                        data-interactive
                        aria-label={t('floor_guide.label.all_floors_guide')}
                    >
                        {t('floor_guide.label.all_floors_guide')}
                    </p>
                )}
            </section>

            <section className={styles['floor__content__wrapper']}>
                <section className={styles['floor__content']}>
                    <section className={styles['floor__actions']} data-region-nav data-region-order={3}>
                        <SecondaryFloorButton
                            tabIndex={3}
                            data-interactive
                            isSelected={isShowAllFloorGuide}
                            aria-label={generateAllFloorsGuideWithTTS(t, t('floor_guide.button.all_floors_guide'), isShowAllFloorGuide)}
                            onClick={() => handleAllFloorsGuide()}
                        >
                            {t('floor_guide.button.all_floors_guide')}
                        </SecondaryFloorButton>
                        <SecondaryFloorButton
                            role="button"
                            data-tts-scope="my-location-button"
                            tabIndex={3}
                            onMouseDown={handleMyLocationMouseDown}
                            onFocus={handleMyLocationFocus}
                            onClick={onShowMyLocation}
                        >
                            {t('floor_guide.button.current_location')}
                        </SecondaryFloorButton>
                    </section>
                    <section className={styles['floor__list']}>
                        {shouldScroll && (
                            <section className={styles['floor__scroll-btn']}>
                                <ArrowFloorButton
                                    data-tts-scope="arrow-floor-button"
                                    data-type="button"
                                    data-region-nav
                                    data-region-order={5}
                                    tabIndex={5}
                                    data-interactive
                                    className="swiper-prev"
                                    aria-disabled={isDisabledPrev}
                                    onClick={() => handleScrollUpWithTTS()}
                                    onFocus={() => {
                                        if (isDisabledPrev) {
                                            speak(`${t('floor_guide.text_to_speech.focus.inactive_prev_floor_btn')} ${inactiveButtonText}`);
                                        } else {
                                            speak(`${t('floor_guide.text_to_speech.focus.active_prev_floor_btn')} ${buttonText}`);
                                        }
                                    }}
                                >
                                    <ChevronUpIcon />
                                </ArrowFloorButton>
                            </section>
                        )}

                        <div ref={swiperContainerRef}>
                            <Swiper
                                className={clsx(styles['floor__list-container'], 'scrollbar-hide h-fit', {
                                    'max-h-[calc(304*var(--vh-unit))]': shouldScroll && isFullscreen,
                                    'max-h-[calc(242*var(--vh-unit))]': shouldScroll && !isFullscreen,
                                })}
                                mousewheel={false}
                                allowTouchMove={true}
                                simulateTouch={false}
                                direction={'vertical'}
                                spaceBetween={scaleHeightPixel(6)}
                                centerInsufficientSlides
                                navigation={{
                                    prevEl: '.swiper-prev',
                                    nextEl: '.swiper-next',
                                }}
                                slidesPerView={1}
                                loop={false}
                                ref={swiperRef}
                                preventInteractionOnTransition
                                onSlideChange={(swiper) => {
                                    handleDisablePrev(swiper.isBeginning);
                                    handleDisableNext(swiper.isEnd);
                                    setCurrentActiveIndex(swiper.activeIndex);
                                }}
                                onInit={(swiper) => {
                                    handleDisablePrev(swiper.isBeginning);
                                    handleDisableNext(swiper.isEnd);
                                    setCurrentActiveIndex(swiper.activeIndex);
                                }}
                            >
                                {floorSlides?.map((floorSlide, slideIndex) => (
                                    <SwiperSlide key={floorSlide.index} className="!h-auto">
                                        <div
                                            className="flex h-fit flex-col gap-[calc(6*var(--vh-unit))]"
                                            data-region-nav={slideIndex === currentActiveIndex}
                                            data-region-order={slideIndex === currentActiveIndex ? 4 : -1}
                                        >
                                            {floorSlide.slideContent.map((floor) => {
                                                const tabIndex = getFloorTabIndex(slideIndex);

                                                return (
                                                    <FloorButton
                                                        key={floor.id}
                                                        data-tts-scope="floor-button"
                                                        tabIndex={tabIndex}
                                                        data-interactive
                                                        role="radio"
                                                        aria-checked={isSelectedItem(floor)}
                                                        isSelected={isSelectedItem(floor)}
                                                        aria-label={generateFloorNameWithTTS(
                                                            t,
                                                            t('floor_guide.text_to_speech.floor', {
                                                                floorName: generateFloorName(floor.name[0]?.text ?? ''),
                                                            }),
                                                            isSelectedItem(floor)
                                                        )}
                                                        onClick={() => handleSelectFloor(floor)}
                                                        onFocus={() => handleFocusFloorBtn(floor, isSelectedItem(floor))}
                                                    >
                                                        {floor.name[0]?.text ?? '-'}
                                                    </FloorButton>
                                                );
                                            })}
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>

                        {shouldScroll && (
                            <section className={clsx(styles['floor__scroll-btn'])}>
                                <ArrowFloorButton
                                    data-tts-scope="arrow-floor-button"
                                    data-type="button"
                                    data-region-nav
                                    data-region-order={6}
                                    tabIndex={5}
                                    data-interactive
                                    className="swiper-next"
                                    aria-disabled={isDisabledNext}
                                    onClick={() => handleScrollDownWithTTS()}
                                    onFocus={() => {
                                        if (isDisabledNext) {
                                            speak(`${t('floor_guide.text_to_speech.focus.inactive_next_floor_btn')} ${inactiveButtonText}`);
                                        } else {
                                            speak(`${t('floor_guide.text_to_speech.focus.active_next_floor_btn')} ${buttonText}`);
                                        }
                                    }}
                                >
                                    <ChevronDownIcon />
                                </ArrowFloorButton>
                            </section>
                        )}
                    </section>
                </section>
            </section>
        </section>
    );
};
